import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { tokenUtils, apiService } from '../utils/api';
import { logregApi } from '../api/logreg';
import cache from '../utils/cache';
import useSessionManager from '../hooks/useSessionManager';
import IdleWarningModal from '../components/common/IdleWarningModal';

const AuthContext = createContext();

// Module-level flag to prevent any duplicate auth checks across all instances
let authCheckExecuted = false;

// eslint-disable-next-line react-refresh/only-export-components -- useAuth must be co-located with AuthContext for convenience
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);
  // Helper function: Call API with automatic retry on timeout (for cold starts)
  const callWithRetry = async (apiCall, timeout = 3000) => {
    let retried = false;

    const attemptCall = async () => {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), timeout)
      );

      try {
        return await Promise.race([apiCall(), timeoutPromise]);
      } catch (error) {
        // If timeout and haven't retried yet, retry once
        if (error.message === 'timeout' && !retried) {
          retried = true;
          return await apiCall(); // Retry without timeout (give it time to wake up)
        }
        throw error;
      }
    };

    return attemptCall();
  };

  useEffect(() => {
    // CRITICAL: Use module-level flag to prevent StrictMode double execution
    if (authCheckExecuted) {
      return;
    }

    authCheckExecuted = true;

    const checkAuth = async () => {
      try {
        const { token, refreshKey } = tokenUtils.getTokens();

        if (token && refreshKey) {
          // SECURE: Validate token with server-side API (with cold start retry)
          const validationResult = await callWithRetry(() => logregApi.validateSession(token));

          if (validationResult.success && validationResult.data) {
            const data = validationResult.data;

            // Determine user type from role (1=owner, 2=manager → 'business'; 3=employee → 'employee')
            const role = data.role || 3;
            let userType = (role === 1 || role === 2) ? 'business' : 'employee';

            setIsAuthenticated(true);
            setUserType(userType);
            setUser({
              authenticated: true,
              userId: data.userId,
              email: data.email,
              type: userType,
              role: role,
              businessId: data.businessId,
              employeeId: data.employeeId,
              accessBusinessAccount: data.accessBusinessAccount,
              accessBookingManagement: data.accessBookingManagement,
              accessPOS: data.accessPOS,
              permissions: data.permissions || [],
              userTypeDetail: data.userType,
              firstName: data.firstName,
              lastName: data.lastName,
              preferredName: data.preferredName,
              jobTitle: data.jobTitle,
              mobileNumber: data.mobileNumber,
              gender: data.gender,
              birthday: data.birthday,
              businessName: data.businessName,
              legalEntityName: data.legalEntityName,
              country: data.country,
              subscriptionStatus: data.subscriptionStatus ?? null,
              subscriptionPlan: data.subscriptionPlan ?? null,
              nextBillingDate: data.nextBillingDate ?? null,
              gracePeriodDays: data.gracePeriodDays ?? 30,
              businessActive: data.businessActive ?? true,
              adminOverrideActive: data.adminOverrideActive ?? false
            });
          } else {
            // Token validation failed - try to refresh using refreshKey
            // Check if error is JWT_INVALID or token expired
            const isTokenInvalid = validationResult.error === 'JWT_INVALID' ||
                                  validationResult.error === 'JWT_EXPIRED' ||
                                  validationResult.error?.includes('expired') ||
                                  validationResult.error?.includes('invalid');

            // Attempt token refresh if we have a refreshKey
            if (refreshKey && isTokenInvalid) {
              try {
                const refreshResult = await callWithRetry(() => logregApi.refreshSessionToken(refreshKey));

                if (refreshResult.success && refreshResult.data) {
                  const newToken = refreshResult.data.sessionToken;
                  const newRefreshKey = refreshResult.data.refreshKey || refreshKey;

                  if (!newToken) {
                    throw new Error('No sessionToken in refresh response');
                  }

                  // Store new tokens (preserve keepSignedIn preference)
                  const keepSignedIn = localStorage.getItem('sessionToken') !== null;
                  tokenUtils.storeTokens(newToken, newRefreshKey, keepSignedIn);

                  // Retry validation with new token
                  const retryValidation = await callWithRetry(() => logregApi.validateSession(newToken));

                  if (retryValidation.success && retryValidation.data) {
                    const data = retryValidation.data;
                    const role = data.role || 3;
                    let userType = (role === 1 || role === 2) ? 'business' : 'employee';

                    setIsAuthenticated(true);
                    setUserType(userType);
                    setUser({
                      authenticated: true,
                      userId: data.userId,
                      email: data.email,
                      type: userType,
                      role: role,
                      businessId: data.businessId,
                      employeeId: data.employeeId,
                      accessBusinessAccount: data.accessBusinessAccount,
                      accessBookingManagement: data.accessBookingManagement,
                      accessPOS: data.accessPOS,
                      permissions: data.permissions || [],
                      userTypeDetail: data.userType,
                      firstName: data.firstName,
                      lastName: data.lastName,
                      preferredName: data.preferredName,
                      jobTitle: data.jobTitle,
                      mobileNumber: data.mobileNumber,
                      gender: data.gender,
                      birthday: data.birthday,
                      businessName: data.businessName,
                      legalEntityName: data.legalEntityName,
                      country: data.country,
                      subscriptionStatus: data.subscriptionStatus ?? null,
                      subscriptionPlan: data.subscriptionPlan ?? null,
                      nextBillingDate: data.nextBillingDate ?? null,
                      gracePeriodDays: data.gracePeriodDays ?? 30,
                      businessActive: data.businessActive ?? true,
                      adminOverrideActive: data.adminOverrideActive ?? false
                    });
                    return; // Success - exit early
                  }
                }

                // If we get here, refresh failed - log out
                tokenUtils.clearTokens();
                setIsAuthenticated(false);
                setUser(null);
                setUserType(null);
              } catch (refreshError) {
                tokenUtils.clearTokens();
                setIsAuthenticated(false);
                setUser(null);
                setUserType(null);
              }
            } else {
              // No refreshKey available or not a token expiry issue - log out
              tokenUtils.clearTokens();
              setIsAuthenticated(false);
              setUser(null);
              setUserType(null);
            }
          }
        } else {
          // No tokens found
          tokenUtils.clearTokens();
          setIsAuthenticated(false);
          setUser(null);
          setUserType(null);
        }
      } catch (error) {
        // Network or other error - fail secure
        tokenUtils.clearTokens();
        setIsAuthenticated(false);
        setUser(null);
        setUserType(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []); // Empty deps - only run once on mount

  const login = async (token, refreshKey, userData = null, keepSignedIn = true) => {
    tokenUtils.storeTokens(token, refreshKey, keepSignedIn);
    setIsAuthenticated(true);

    // userData MUST be provided by the login endpoint
    if (userData) {
      // Determine user type from role (1=owner, 2=manager → 'business'; 3=employee → 'employee')
      const role = userData.role || 3;
      let userType = (role === 1 || role === 2) ? 'business' : 'employee';

      // For employees, fetch profile to get name data if not already provided
      let profileData = {};
      if (userType === 'employee' && userData.employeeId && !userData.preferredName) {
        try {
          const profileResult = await callWithRetry(() => apiService.getEmployeeProfile(token, userData.employeeId));
          if (profileResult.success && profileResult.data) {
            profileData = {
              firstName: profileResult.data.firstName,
              lastName: profileResult.data.lastName,
              preferredName: profileResult.data.preferredName
            };
          }
        } catch (error) {
          // Silently handle profile load failure - not critical
        }
      }

      setUserType(userType);
      setUser({
        ...userData,
        ...profileData,
        authenticated: true,
        type: userType,
        role: role
      });
    } else {
      // SECURITY: userData must be provided - no fallback
      tokenUtils.clearTokens();
      setIsAuthenticated(false);
      setUser(null);
      setUserType(null);
    }
  };

  const logout = () => {
    tokenUtils.clearTokens();
    cache.clear(); // SECURITY: Clear all cached data on logout
    queryClient.clear();
    setIsAuthenticated(false);
    setUser(null);
    setUserType(null);
  };

  // Session Manager: handles proactive token refresh and idle timeout
  const {
    showIdleWarning,
    countdown,
    stayLoggedIn,
    handleIdleLogout
  } = useSessionManager(isAuthenticated, logout);

  // Computed: does this user have full app access?
  // True when subscription is Active (1), Trial (0), PastDue but still in grace period (2 + business active), or admin override
  const hasFullAccess =
    user?.subscriptionStatus === 1 ||                                    // Active
    user?.subscriptionStatus === 0 ||                                    // Trial
    (user?.subscriptionStatus === 2 && user?.businessActive !== false) || // PastDue but in grace period
    user?.adminOverrideActive === true;

  // Is the user in the grace period? (PastDue subscription, business not yet deactivated)
  const isInGracePeriod = user?.subscriptionStatus === 2 && user?.businessActive !== false;

  const value = {
    isAuthenticated,
    user,
    userType,
    login,
    logout,
    loading,
    hasFullAccess,
    isInGracePeriod
  };

  return (
    <AuthContext.Provider value={value}>
      {/* Idle Warning Modal - shown across all pages when user is idle */}
      <IdleWarningModal
        show={showIdleWarning}
        countdown={countdown}
        onStayLoggedIn={stayLoggedIn}
        onLogout={handleIdleLogout}
      />
      {children}
    </AuthContext.Provider>
  );
};